import { useCallback, useEffect, useRef } from "react";
import * as fabric from "fabric";
import { CANVAS_CONFIG } from "../constants/designConstants";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "@/hooks/useCanvas";
import canvasStorageManager from "@/utils/canvasStorageManager";
import { canvasSyncManager } from "@/utils/canvasSyncManager";

export const useTshirtCanvas = ({ svgPath, view, onDesignUpdate }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const tshirtColor = useSelector((state) => state.tshirt.tshirtColor);
  const selectedView = useSelector((state) => state.tshirt.selectedView);
  const dispatch = useDispatch();

  const { setActiveCanvas, setSelectedObject, setFrontCanvas, setBackCanvas } =
    useCanvas();

  // Function to save canvas objects
  const saveCanvas = useCallback(() => {
    if (fabricCanvasRef.current) {
      canvasStorageManager.saveCanvasObjects(view, fabricCanvasRef.current);
    }
  }, [view]);

  // Function to notify design changes
  const notifyDesignChange = useCallback(() => {
    if (fabricCanvasRef.current && onDesignUpdate) {
      const textureDataUrl = canvasSyncManager.getCanvasTexture(
        fabricCanvasRef.current
      );
      onDesignUpdate(textureDataUrl);
    }
  }, [onDesignUpdate]);

  // Auto-save on object changes
  const handleObjectChange = useCallback(() => {
    saveCanvas();
    notifyDesignChange();
  }, [saveCanvas, notifyDesignChange]);

  // Initialize Fabric.js Canvas (runs once per mount)
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    const canvas = new fabric.Canvas(canvasRef.current, {
      ...CANVAS_CONFIG,
      preserveObjectStacking: true,
    });

    fabricCanvasRef.current = canvas;

    // Register canvas in context
    if (view === "front") setFrontCanvas(canvas);
    if (view === "back") setBackCanvas(canvas);

    // Load saved objects only once
    if (!hasLoadedRef.current) {
      const savedObjects = canvasStorageManager.loadCanvasObjects(view);
      if (savedObjects && savedObjects.length > 0) {
        savedObjects.forEach((obj) => addFabricObject(canvas, obj));
        canvas.renderAll();
      }
      hasLoadedRef.current = true;
    }

    // Handle Object Selection
    const handleSelectionCreated = (e) => setSelectedObject(e.selected[0]);
    const handleSelectionUpdated = (e) => setSelectedObject(e.selected[0]);
    const handleSelectionCleared = () => setSelectedObject(null);

    canvas.on("selection:created", handleSelectionCreated);
    canvas.on("selection:updated", handleSelectionUpdated);
    canvas.on("selection:cleared", handleSelectionCleared);

    // Listen for any changes on the canvas
    canvas.on("object:modified", handleObjectChange);
    canvas.on("object:added", handleObjectChange);
    canvas.on("object:removed", handleObjectChange);

    // Save canvas data when the page is about to unload
    const handleBeforeUnload = () => saveCanvas();
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup
    return () => {
      saveCanvas();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      canvas.off("selection:created", handleSelectionCreated);
      canvas.off("selection:updated", handleSelectionUpdated);
      canvas.off("selection:cleared", handleSelectionCleared);
      canvas.off("object:modified", handleObjectChange);
      canvas.off("object:added", handleObjectChange);
      canvas.off("object:removed", handleObjectChange);
      canvas.dispose();
      fabricCanvasRef.current = null;
      hasLoadedRef.current = false;
    };
  }, []); // Empty dependency - runs once on mount

  // Switch Active Canvas When View Changes
  useEffect(() => {
    if (selectedView === view && fabricCanvasRef.current) {
      setActiveCanvas(fabricCanvasRef.current);
      fabricCanvasRef.current.renderAll();
    }
  }, [selectedView, view, setActiveCanvas]);

  // Load SVG ClipPath
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !svgPath) return;

    const clipPath = new fabric.Path(svgPath);
    const scale = CANVAS_CONFIG.height / 810;
    clipPath.set({
      scaleX: scale * 0.9,
      scaleY: scale * 0.9,
      left: 5,
      top: 64,
      originX: "left",
      originY: "top",
      absolutePositioned: true,
    });

    canvas.clipPath = clipPath;
    canvas.renderAll();
  }, [svgPath]);

  return { canvasRef, fabricCanvasRef, tshirtColor };
};

// Helper function to add objects to canvas
const addFabricObject = (canvas, objectData) => {
  switch (objectData.type) {
    case "Line":
      canvas.add(
        new fabric.Line(
          [objectData.x1, objectData.y1, objectData.x2, objectData.y2],
          {
            left: objectData.left || 0,
            top: objectData.top || 0,
            stroke: objectData.stroke || "black",
            strokeWidth: objectData.strokeWidth || 2,
            strokeLineCap: objectData.strokeLineCap || "round",
            strokeLineJoin: objectData.strokeLineJoin || "miter",
            opacity: objectData.opacity || 1,
            angle: objectData.angle || 0,
            scaleX: objectData.scaleX || 1,
            scaleY: objectData.scaleY || 1,
          }
        )
      );
      break;
    case "Textbox":
      const textbox = new fabric.Textbox(objectData.text, {
        left: objectData.left,
        top: objectData.top,
        width: objectData.width,
        fontSize: objectData.fontSize,
        fontFamily: objectData.fontFamily,
        textAlign: objectData.textAlign,
        fill: objectData.fill,
        scaleX: objectData.scaleX,
        scaleY: objectData.scaleY,
        angle: objectData.angle,
        opacity: objectData.opacity,
      });

      textbox.initDimensions();
      canvas.add(textbox);
      break;
    case "Image":
      if (!objectData.src || !objectData.src.startsWith("data:image")) return;
      const imgElement = new Image();
      imgElement.src = objectData.src;
      imgElement.onload = () => {
        const fabricImg = new fabric.Image(imgElement, {
          left: objectData.left || 0,
          top: objectData.top || 0,
          scaleX: objectData.scaleX || 1,
          scaleY: objectData.scaleY || 1,
          angle: objectData.angle || 0,
          opacity: objectData.opacity || 1,
        });
        canvas.add(fabricImg);
        canvas.renderAll();
      };
      break;
  }
};
